import { useEffect, useRef, useState } from 'react';

export default function HomePage() {
  const [sliderPosition, setSliderPosition] = useState(50);
  const sliderRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  // Schema.org JSON-LD
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

    const localBusiness = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      '@id': `${siteUrl}/#business`,
      name: 'RealtyFlicks.ca',
      url: siteUrl,
      email: 'info@realtyflicks.ca',
      description:
        'Premium real estate photography, drone video, social media clips, scripting, narration, and custom music services in Ottawa, Ontario. Certified and fully insured drone pilot.',
      image: `${siteUrl}/og-image.jpg`,
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Ottawa',
        addressRegion: 'ON',
        addressCountry: 'CA',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 45.4215,
        longitude: -75.6972,
      },
      areaServed: [
        'Ottawa',
        'Arnprior',
        'Smiths Falls',
        'Crysler',
        'Limoges',
        'Casselman',
        'Rockland',
        'Cornwall',
        'Dunrobin',
      ].map((name) => ({ '@type': 'City', name })),
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Real Estate Media Services',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Professional Photo & Video Walkthroughs' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Drone Videos (Interior & Exterior)' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Social Media Specialty Videos' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Scripting & Professional Narration' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Custom Music Selection & Integration' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Specialty Photography (Dusk/Sunset)' } },
        ],
      },
      sameAs: [
        'https://www.instagram.com/realtyflicksca',
        'https://www.youtube.com/@realtyflicksca',
      ],
    };

    const webSite = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${siteUrl}/#website`,
      url: siteUrl,
      name: 'RealtyFlicks.ca',
      description: 'Premium real estate media services in Ottawa, Ontario.',
      publisher: { '@id': `${siteUrl}/#business` },
    };

    const webPage = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${siteUrl}/#webpage`,
      url: siteUrl,
      name: 'Ottawa Real Estate Photography & Drone Video | RealtyFlicks.ca',
      description:
        'RealtyFlicks.ca offers premium real estate photography, drone video, social media clips, and custom music in Ottawa, Ontario.',
      isPartOf: { '@id': `${siteUrl}/#website` },
      about: { '@id': `${siteUrl}/#business` },
      inLanguage: 'en-CA',
    };

    const scripts = [localBusiness, webSite, webPage];
    const ids = ['schema-local-business', 'schema-website', 'schema-webpage'];

    scripts.forEach((data, i) => {
      let el = document.getElementById(ids[i]);
      if (!el) {
        el = document.createElement('script');
        el.id = ids[i];
        (el as HTMLScriptElement).type = 'application/ld+json';
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(data);
    });

    return () => {
      ids.forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, []);

  const handleMouseDown = () => setIsDragging(true);
  const handleMouseUp = () => setIsDragging(false);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging || !sliderRef.current) return;
    const rect = sliderRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const percent = (x / rect.width) * 100;
    setSliderPosition(percent);
  };

  useEffect(() => {
    const handleGlobalMouseUp = () => setIsDragging(false);
    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    setFormMessage('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Validate textarea length
    const message = formData.get('message') as string;
    if (message && message.length > 500) {
      setFormStatus('error');
      setFormMessage('Message must be 500 characters or less.');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d6jmbmqpaf6ievb71i90', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        setFormMessage('Thank you! Your message has been sent successfully.');
        form.reset();
      } else {
        setFormStatus('error');
        setFormMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      setFormStatus('error');
      setFormMessage('Failed to send message. Please try again.');
    }
  };

  return (
    <div className="bg-[#0a0a0f] min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://static.readdy.ai/image/4290f71dfbaa025f6a9f1c6e0e6bc4ce/9f9c33b783b307a3d8f65e1a86b6cfbf.jpeg"
            alt="Luxury home aerial view at dusk"
            className="w-full h-full object-cover scale-110"
          />
          <div className="absolute inset-0 bg-[#0a0a0f]/10"></div>
        </div>

        <div className="absolute inset-0 z-[1] opacity-[0.03]"></div>

        {/* Navigation */}
        <nav className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-6 md:px-16 py-6">
          <div className="text-white font-bold text-xl tracking-tight">
            <span className="text-[#2a7dd4]">Realty</span>Flicks<span className="text-[#2a7dd4]">.ca</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <button
              onClick={() => scrollToSection('services')}
              className="text-white/80 hover:text-[#2a7dd4] text-sm font-medium tracking-wide transition-colors duration-200 cursor-pointer"
            >
              Services
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className="text-white/80 hover:text-[#2a7dd4] text-sm font-medium tracking-wide transition-colors duration-200 cursor-pointer"
            >
              About
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="text-white/80 hover:text-[#2a7dd4] text-sm font-medium tracking-wide transition-colors duration-200 cursor-pointer"
            >
              Contact
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="bg-[#2a7dd4] text-black text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-[#e2c06a] transition-all duration-200 hover:shadow-lg hover:shadow-[#2a7dd4]/30 whitespace-nowrap cursor-pointer"
            >
              Get a Free Quote
            </button>
          </div>
        </nav>

        {/* Hero Content */}
        <div className="relative z-10 text-center px-4 sm:px-6 max-w-7xl mx-auto pt-20 w-full">
          <p className="text-sm sm:text-base text-white mb-4 mt-8 sm:mt-0 font-medium italic px-2 [text-shadow:0_2px_8px_rgba(0,0,0,0.85)]">
            Premium Real Estate Photography &amp; Drone Videos
          </p>

          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white/90 text-xs font-medium px-3 sm:px-4 py-2 rounded-full mb-8 sm:mb-12">
            <span className="w-2 h-2 rounded-full bg-[#2a7dd4] animate-pulse"></span>
            Certified & Fully Insured Drone Pilot
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-extrabold text-white leading-[1.1] tracking-tight mb-6 [text-shadow:0_2px_12px_rgba(0,0,0,0.9)]">
            Elevate Your Listings
          </h1>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-2">
            <button
              onClick={() => scrollToSection('contact')}
              className="bg-[#2a7dd4] text-black font-bold px-6 sm:px-8 py-3.5 sm:py-4 rounded-full text-sm sm:text-base hover:bg-[#e2c06a] transition-all duration-300 hover:shadow-2xl hover:shadow-[#2a7dd4]/40 hover:-translate-y-0.5 transform whitespace-nowrap cursor-pointer"
            >
              Get a Free Quote
            </button>
            <button
              onClick={() => scrollToSection('services')}
              className="border border-white/30 text-white font-semibold px-6 sm:px-8 py-3.5 sm:py-4 rounded-full text-sm sm:text-base hover:bg-white/10 hover:border-white/60 transition-all duration-300 backdrop-blur-sm whitespace-nowrap cursor-pointer"
            >
              View Our Services
            </button>
          </div>
        </div>

        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2">
          <span className="text-white/40 text-xs tracking-widest uppercase">Scroll</span>
          <div className="w-px h-10 bg-gradient-to-b from-white/40 to-transparent animate-pulse"></div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-28 px-6 relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#2a7dd4]/5 rounded-full blur-[120px] pointer-events-none"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <div className="inline-block text-[#2a7dd4] text-xs font-bold tracking-[0.2em] uppercase mb-4 px-4 py-1.5 border border-[#2a7dd4]/30 rounded-full">
              What We Offer
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4 tracking-tight">
              Every Service Your Listing Needs
            </h2>
            <p className="text-white/50 text-lg max-w-xl mx-auto">
              Full packages help listings stand out and close deals quicker.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            <div className="group relative bg-white/[0.03] border border-white/[0.07] rounded-2xl p-7 hover:bg-white/[0.07] hover:border-[#2a7dd4]/30 transition-all duration-300 cursor-default md:col-span-2 lg:col-span-1">
              <span className="absolute top-5 right-5 text-[10px] font-bold text-[#2a7dd4] bg-[#2a7dd4]/10 border border-[#2a7dd4]/20 px-2.5 py-1 rounded-full">
                Most Popular
              </span>
              <div className="text-4xl mb-5 group-hover:scale-110 transition-transform duration-300 inline-block">
                📸
              </div>
              <h3 className="text-white font-bold text-lg mb-3 leading-snug">
                Professional Photo & Video Walkthroughs
              </h3>
              <p className="text-white/50 text-sm leading-relaxed">
                Cinematic interior/exterior captures of every detail — from grand entrances to intimate spaces.
              </p>
              <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#2a7dd4]/0 to-transparent group-hover:via-[#2a7dd4]/40 transition-all duration-500 rounded-b-2xl"></div>
            </div>

            <div className="group relative bg-white/[0.03] border border-white/[0.07] rounded-2xl p-7 hover:bg-white/[0.07] hover:border-[#2a7dd4]/30 transition-all duration-300 cursor-default">
              <span className="absolute top-5 right-5 text-[10px] font-bold text-[#2a7dd4] bg-[#2a7dd4]/10 border border-[#2a7dd4]/20 px-2.5 py-1 rounded-full">
                Licensed & Insured
              </span>
              <div className="text-4xl mb-5 group-hover:scale-110 transition-transform duration-300 inline-block">
                🚁
              </div>
              <h3 className="text-white font-bold text-lg mb-3 leading-snug">
                Drone Videos (Interior & Exterior)
              </h3>
              <p className="text-white/50 text-sm leading-relaxed">
                Dynamic aerial views showcasing layout, grounds, and the wow factor that makes buyers stop scrolling.
              </p>
              <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#2a7dd4]/0 to-transparent group-hover:via-[#2a7dd4]/40 transition-all duration-500 rounded-b-2xl"></div>
            </div>

            <div className="group relative bg-white/[0.03] border border-white/[0.07] rounded-2xl p-7 hover:bg-white/[0.07] hover:border-[#2a7dd4]/30 transition-all duration-300 cursor-default">
              <div className="text-4xl mb-5 group-hover:scale-110 transition-transform duration-300 inline-block">
                📱
              </div>
              <h3 className="text-white font-bold text-lg mb-3 leading-snug">
                Social Media Specialty Videos
              </h3>
              <p className="text-white/50 text-sm leading-relaxed">
                Short, engaging clips optimized for Instagram Reels, TikTok, Facebook — built to go viral.
              </p>
              <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#2a7dd4]/0 to-transparent group-hover:via-[#2a7dd4]/40 transition-all duration-500 rounded-b-2xl"></div>
            </div>

            <div className="group relative bg-white/[0.03] border border-white/[0.07] rounded-2xl p-7 hover:bg-white/[0.07] hover:border-[#2a7dd4]/30 transition-all duration-300 cursor-default">
              <div className="text-4xl mb-5 group-hover:scale-110 transition-transform duration-300 inline-block">
                🎙️
              </div>
              <h3 className="text-white font-bold text-lg mb-3 leading-snug">
                Scripting & Professional Narration
              </h3>
              <p className="text-white/50 text-sm leading-relaxed">
                Compelling voiceover stories that highlight features, evoke emotion, and move buyers to act.
              </p>
              <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#2a7dd4]/0 to-transparent group-hover:via-[#2a7dd4]/40 transition-all duration-500 rounded-b-2xl"></div>
            </div>

            <div className="group relative bg-white/[0.03] border border-white/[0.07] rounded-2xl p-7 hover:bg-white/[0.07] hover:border-[#2a7dd4]/30 transition-all duration-300 cursor-default">
              <div className="text-4xl mb-5 group-hover:scale-110 transition-transform duration-300 inline-block">
                🎵
              </div>
              <h3 className="text-white font-bold text-lg mb-3 leading-snug">
                Custom Music Selection & Integration
              </h3>
              <p className="text-white/50 text-sm leading-relaxed">
                Tailored tracks matched to the property vibe — every note boosting emotional impact and memorability.
              </p>
              <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#2a7dd4]/0 to-transparent group-hover:via-[#2a7dd4]/40 transition-all duration-500 rounded-b-2xl"></div>
            </div>

            <div className="group relative bg-white/[0.03] border border-white/[0.07] rounded-2xl p-7 hover:bg-white/[0.07] hover:border-[#2a7dd4]/30 transition-all duration-300 cursor-default">
              <div className="text-4xl mb-5 group-hover:scale-110 transition-transform duration-300 inline-block">
                🌅
              </div>
              <h3 className="text-white font-bold text-lg mb-3 leading-snug">Specialty Photography</h3>
              <p className="text-white/50 text-sm leading-relaxed">
                Dramatic dusk/sunset shots, weather-enhanced views, and creative angles that make listings stand apart.
              </p>
              <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#2a7dd4]/0 to-transparent group-hover:via-[#2a7dd4]/40 transition-all duration-500 rounded-b-2xl"></div>
            </div>
          </div>

          <div className="text-center mt-14">
            <button
              onClick={() => scrollToSection('contact')}
              className="bg-[#2a7dd4] text-black font-bold px-10 py-4 rounded-full text-base hover:bg-[#e2c06a] transition-all duration-300 hover:shadow-2xl hover:shadow-[#2a7dd4]/30 hover:-translate-y-0.5 transform whitespace-nowrap cursor-pointer"
            >
              Book Your Package
            </button>
          </div>
        </div>
      </section>

      {/* Before & After Section */}
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-block text-[#2a7dd4] text-xs font-bold tracking-[0.2em] uppercase mb-4 px-4 py-1.5 border border-[#2a7dd4]/30 rounded-full">
              Before & After
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-3 tracking-tight">
              See the{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2a7dd4] to-[#6db3f2]">
                Difference
              </span>
            </h2>
            <p className="text-white/50 text-base">Drag the slider to compare day vs. dusk photography.</p>
          </div>

          <div
            ref={sliderRef}
            className="relative w-full aspect-[16/10] rounded-2xl overflow-hidden cursor-col-resize select-none shadow-2xl shadow-black/60 border border-white/[0.07]"
            onMouseMove={handleMouseMove}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
          >
            <img
              src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/69a75af7603fddbdd7ad44f8/52203f931_hf_20260303_165030_8cd8a241-2eb7-4fdd-95db-b95eec16c15a.png"
              alt="Day view"
              className="absolute inset-0 w-full h-full object-cover pointer-events-none"
              draggable={false}
            />
            <div
              className="absolute inset-0 overflow-hidden pointer-events-none"
              style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
            >
              <img
                src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/69a75af7603fddbdd7ad44f8/02102ec16_hf_20260303_170005_939772c1-7048-43e6-93bc-2d58f6b7e7c2.jpg"
                alt="Dusk view"
                className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                draggable={false}
              />
            </div>
            <div
              className="absolute top-0 bottom-0 w-0.5 bg-white shadow-[0_0_12px_rgba(255,255,255,0.6)]"
              style={{ left: `${sliderPosition}%` }}
            ></div>
            <div
              className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-11 h-11 bg-white rounded-full shadow-xl flex items-center justify-center cursor-col-resize z-10 border-2 border-white/80"
              style={{ left: `${sliderPosition}%` }}
              onMouseDown={handleMouseDown}
            >
              <div className="flex gap-1">
                <div className="w-0.5 h-4 bg-gray-400"></div>
                <div className="w-0.5 h-4 bg-gray-400"></div>
              </div>
            </div>
            <div className="absolute top-4 left-4 bg-black/50 backdrop-blur-sm text-white text-xs font-bold px-3 py-1.5 rounded-full border border-white/20">
              🌅 Dusk
            </div>
            <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-sm text-white text-xs font-bold px-3 py-1.5 rounded-full border border-white/20">
              ☀️ Day
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 sm:py-28 px-4 sm:px-6 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1600&q=70&fit=crop"
            alt="Ontario property"
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0f] via-transparent to-[#0a0a0f]"></div>
        </div>

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-16 items-center">
            <div className="relative pb-8 sm:pb-0">
              <div className="relative rounded-2xl overflow-hidden aspect-[4/3] shadow-2xl shadow-black/60">
                <img
                  src="https://images.unsplash.com/photo-1580587771525-78b9dba3b914?w=900&q=85&fit=crop"
                  alt="Aerial view of Ontario home"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-black/30 to-transparent"></div>
              </div>
              <div className="absolute -bottom-4 -right-4 sm:-bottom-6 sm:-right-6 bg-[#2a7dd4] text-white rounded-2xl p-4 sm:p-5 shadow-xl shadow-[#2a7dd4]/20 max-w-[160px] sm:max-w-[200px]">
                <div className="text-3xl font-black leading-none mb-1">🏆</div>
                <div className="font-bold text-sm leading-tight">Certified Transport Canada Drone Pilot</div>
              </div>
            </div>

            <div>
              <div className="inline-block text-[#2a7dd4] text-xs font-bold tracking-[0.2em] uppercase mb-4 px-4 py-1.5 border border-[#2a7dd4]/30 rounded-full">
                Why Choose Us
              </div>
              <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-6 leading-tight tracking-tight">
                Premium Media for{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2a7dd4] to-[#6db3f2]">
                  Real Estate
                </span>{' '}
                Pros
              </h2>
              <p className="text-white/60 text-lg leading-relaxed mb-6">
                At RealtyFlicks.ca, we specialize in premium visual and audio media for real estate. As a certified
                and fully insured drone pilot based in Ottawa, Ontario, we deliver safe, legal, high-quality work with
                fast turnaround. Serving luxury homes, condos, rural properties, and commercial spaces across the
                Ottawa region and beyond.
              </p>

              <div className="mb-8">
                <p className="text-white/40 text-xs uppercase tracking-widest font-semibold mb-3">Areas We Serve</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    'Ottawa',
                    'Arnprior',
                    'Smiths Falls',
                    'Crysler',
                    'Limoges',
                    'Casselman',
                    'Rockland',
                    'Cornwall',
                    'Dunrobin',
                  ].map((area) => (
                    <span
                      key={area}
                      className="bg-white/[0.05] border border-[#2a7dd4]/20 text-[#2a7dd4]/80 text-xs font-medium px-3 py-1.5 rounded-full"
                    >
                      {area}
                    </span>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-white/[0.04] border border-white/[0.07] rounded-xl p-4 text-center">
                  <div className="text-2xl font-black text-[#2a7dd4] mb-1">100%</div>
                  <div className="text-white/50 text-xs font-medium">Licensed & Insured</div>
                </div>
                <div className="bg-white/[0.04] border border-white/[0.07] rounded-xl p-4 text-center">
                  <div className="text-2xl font-black text-[#2a7dd4] mb-1">Fast</div>
                  <div className="text-white/50 text-xs font-medium">Turnaround Times</div>
                </div>
                <div className="bg-white/[0.04] border border-white/[0.07] rounded-xl p-4 text-center">
                  <div className="text-2xl font-black text-[#2a7dd4] mb-1">Ottawa</div>
                  <div className="text-white/50 text-xs font-medium">Based In</div>
                </div>
                <div className="bg-white/[0.04] border border-white/[0.07] rounded-xl p-4 text-center">
                  <div className="text-2xl font-black text-[#2a7dd4] mb-1">9+</div>
                  <div className="text-white/50 text-xs font-medium">Areas Served</div>
                </div>
              </div>

              <div className="space-y-3">
                {[
                  'Safe, TC-certified drone operations — always legal',
                  'Serving all property types: luxury, rural, condo, commercial',
                  'Fast delivery so your listing goes live sooner',
                  'Locally rooted in Ottawa & the surrounding region',
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-[#2a7dd4]/20 border border-[#2a7dd4]/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#2a7dd4]"></div>
                    </div>
                    <span className="text-white/70 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-28 px-6 relative overflow-hidden">
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-[#2a7dd4]/8 rounded-full blur-[100px] pointer-events-none"></div>

        <div className="max-w-5xl mx-auto relative z-10">
          <div className="text-center mb-14">
            <div className="inline-block text-[#2a7dd4] text-xs font-bold tracking-[0.2em] uppercase mb-4 px-4 py-1.5 border border-[#2a7dd4]/30 rounded-full">
              Get in Touch
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4 tracking-tight leading-tight">
              Ready to Make Your Next Listing{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2a7dd4] to-[#6db3f2]">
                Unforgettable?
              </span>
            </h2>
            <div className="flex flex-wrap justify-center gap-4 mt-4">
              <span className="text-white/50 text-sm flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#2a7dd4] inline-block"></span>
                Fast turnaround
              </span>
              <span className="text-white/50 text-sm flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#2a7dd4] inline-block"></span>
                Competitive packages
              </span>
              <span className="text-white/50 text-sm flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#2a7dd4] inline-block"></span>
                Proudly local to Ottawa & surrounding areas
              </span>
            </div>
          </div>

          <div className="bg-white/[0.04] border border-white/[0.08] rounded-3xl p-8 md:p-12 backdrop-blur-sm">
            <form className="space-y-6" data-readdy-form id="contact-form" onSubmit={handleFormSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-white/50 text-xs font-semibold uppercase tracking-wider mb-2">
                    Full Name *
                  </label>
                  <input
                    name="name"
                    required
                    placeholder="Jane Smith"
                    className="w-full bg-white/[0.05] border border-white/[0.1] text-white placeholder:text-white/25 rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:border-[#2a7dd4]/60 focus:bg-white/[0.08] transition-all duration-200"
                  />
                </div>
                <div>
                  <label className="block text-white/50 text-xs font-semibold uppercase tracking-wider mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="jane@realty.com"
                    className="w-full bg-white/[0.05] border border-white/[0.1] text-white placeholder:text-white/25 rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:border-[#2a7dd4]/60 focus:bg-white/[0.08] transition-all duration-200"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-white/50 text-xs font-semibold uppercase tracking-wider mb-2">
                    Property Type / Location
                  </label>
                  <input
                    name="propertyType"
                    placeholder="e.g. Luxury Home, Renfrew ON"
                    className="w-full bg-white/[0.05] border border-white/[0.1] text-white placeholder:text-white/25 rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:border-[#2a7dd4]/60 focus:bg-white/[0.08] transition-all duration-200"
                  />
                </div>
              </div>
              <div>
                <label className="block text-white/50 text-xs font-semibold uppercase tracking-wider mb-2">
                  Message / Services Needed
                </label>
                <textarea
                  name="message"
                  rows={5}
                  maxLength={500}
                  placeholder="Tell us about the property and what you're looking for..."
                  className="w-full bg-white/[0.05] border border-white/[0.1] text-white placeholder:text-white/25 rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:border-[#2a7dd4]/60 focus:bg-white/[0.08] transition-all duration-200 resize-none"
                ></textarea>
              </div>

              {formStatus === 'success' && (
                <div className="bg-green-500/10 border border-green-500/30 text-green-400 px-4 py-3 rounded-xl text-sm">
                  {formMessage}
                </div>
              )}

              {formStatus === 'error' && (
                <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-xl text-sm">
                  {formMessage}
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4 pt-2">
                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="flex-1 bg-[#2a7dd4] text-black font-bold py-4 rounded-xl text-base hover:bg-[#e2c06a] transition-all duration-300 hover:shadow-xl hover:shadow-[#2a7dd4]/30 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {formStatus === 'submitting' ? 'Sending...' : 'Get a Custom Quote'}
                </button>
                <button
                  type="button"
                  onClick={() => scrollToSection('services')}
                  className="flex-1 border border-[#2a7dd4]/40 text-[#2a7dd4] font-semibold py-4 rounded-xl text-base hover:bg-[#2a7dd4]/10 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  Free Consultation
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/[0.06] bg-black/40 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
            <div className="md:col-span-2">
              <div className="text-white font-bold text-2xl tracking-tight mb-3">
                <span className="text-[#2a7dd4]">Realty</span>Flicks<span className="text-[#2a7dd4]">.ca</span>
              </div>
              <p className="text-white/40 text-sm leading-relaxed max-w-xs mb-6">
                Premium real estate media services in Ontario. Helping agents and sellers make listings irresistible.
              </p>
              <div className="flex gap-3">
                <button
                  title="Instagram"
                  className="w-9 h-9 rounded-lg bg-white/[0.05] border border-white/[0.08] flex items-center justify-center text-sm hover:bg-[#2a7dd4]/20 hover:border-[#2a7dd4]/30 transition-all duration-200 cursor-pointer"
                >
                  📷
                </button>
                <button
                  title="YouTube"
                  className="w-9 h-9 rounded-lg bg-white/[0.05] border border-white/[0.08] flex items-center justify-center text-sm hover:bg-[#2a7dd4]/20 hover:border-[#2a7dd4]/30 transition-all duration-200 cursor-pointer"
                >
                  🎬
                </button>
              </div>
            </div>

            <div>
              <h4 className="text-white font-semibold text-sm mb-5 uppercase tracking-wider">Navigation</h4>
              <ul className="space-y-3">
                <li>
                  <button
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="text-white/40 text-sm hover:text-[#2a7dd4] transition-colors duration-200 cursor-pointer"
                  >
                    Home
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => scrollToSection('services')}
                    className="text-white/40 text-sm hover:text-[#2a7dd4] transition-colors duration-200 cursor-pointer"
                  >
                    Services
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => scrollToSection('about')}
                    className="text-white/40 text-sm hover:text-[#2a7dd4] transition-colors duration-200 cursor-pointer"
                  >
                    About
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => scrollToSection('contact')}
                    className="text-white/40 text-sm hover:text-[#2a7dd4] transition-colors duration-200 cursor-pointer"
                  >
                    Contact
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold text-sm mb-5 uppercase tracking-wider">Contact</h4>
              <ul className="space-y-3 text-sm text-white/40">
                <li className="flex items-center gap-2">
                  <span>📧</span>
                  <a
                    href="mailto:info@realtyflicks.ca"
                    className="hover:text-[#2a7dd4] transition-colors duration-200"
                  >
                    info@realtyflicks.ca
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <span>📍</span>
                  <span>Ottawa, Ontario, Canada</span>
                </li>
              </ul>
              <div className="mt-5 inline-flex items-center gap-2 bg-[#2a7dd4]/10 border border-[#2a7dd4]/20 text-[#2a7dd4] text-xs px-3 py-1.5 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-[#2a7dd4] animate-pulse"></span>
                Certified & Fully Insured Drone Pilot
              </div>
            </div>
          </div>

          <div className="border-t border-white/[0.06] pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-white/25 text-xs">© 2026 RealtyFlicks.ca — All rights reserved.</p>
            <p className="text-white/25 text-xs">
              Serving Ottawa, Arnprior, Smiths Falls, Rockland, Cornwall & surrounding areas
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}